import firebase from 'firebase/app';
import { useEffect, useState } from 'react';

import { SHOW_LOGS } from '../../features/App/App';
import { Offer } from '../../models/offers.model';
import { firestore } from '../firebase';
import { FirestoreCollection } from '../firebase.models';

import { useProcessDates } from './useProcessDates.hook';

export function useGetOffersByUid(uid: string): [Offer[], Offer[], boolean, firebase.FirebaseError | null] {
  const [error, setError] = useState<firebase.FirebaseError | null>(null);
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [favorites, setFavorites] = useState<Offer[]>([]);

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      return;
    }

    const unsubscribeFunctions: (() => void)[] = [];

    const fetchData = async () => {
      try {
        const userDocRef = firestore.collection(FirestoreCollection.USERS).doc(uid);
        const userDoc = await userDocRef.get();

        if (!userDoc.exists) {
          SHOW_LOGS && console.error('User not found:', uid);
          setLoading(false);
          return;
        }

        const userData = userDoc.data();
        const offersId = userData?.offers || [];
        const favoritesIds = userData?.favorites || [];

        unsubscribeFunctions.push(fetchOffersByIds(offersId, setOffers), fetchOffersByIds(favoritesIds, setFavorites));
      } catch (err) {
        SHOW_LOGS && console.error('Error fetching offers:', err);
        setError(err as firebase.FirebaseError);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      unsubscribeFunctions.forEach((unsubscribe) => unsubscribe());
    };
  }, [uid]);

  const fetchOffersByIds = (
    offerIds: string[],
    setState: React.Dispatch<React.SetStateAction<Offer[]>>
  ): (() => void) => {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    if (!offerIds.length) return () => {};

    const chunkSize = 10;
    const chunks = [];

    for (let i = 0; i < offerIds.length; i += chunkSize) {
      chunks.push(offerIds.slice(i, i + chunkSize));
    }

    const unsubscribes = chunks.map((chunk) =>
      firestore
        .collection(FirestoreCollection.OFFERS_PUBLIC)
        .where('id', 'in', chunk)
        .onSnapshot((snapshot) => {
          const chunkOffers = snapshot.docs.map((doc) => mapOffer(doc));
          setState((prevOffers) => {
            const updatedOffers = [
              ...prevOffers.filter((o) => !chunkOffers.some((co) => co.id === o.id)),
              ...chunkOffers,
            ];
            return updatedOffers;
          });
        })
    );

    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  };

  const mapOffer = (doc: firebase.firestore.DocumentSnapshot): Offer => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      ...useProcessDates({
        availability: data?.availability,
        availabilityTo: data?.availabilityTo,
        offerValidTo: data?.offerValidTo,
        createdAt: data?.createdAt,
        lastModified: data?.lastModified,
      }),
    } as Offer;
  };

  return [offers, favorites, loading, error];
}
